import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Brain, Dumbbell, Heart, Zap, TriangleAlert } from 'lucide-react';
import cx from 'classnames';
import styles from './AthleteReadinessView.module.scss';
import {
  useAthleteReadiness,
  getZone,
  getZoneBadgeColor,
  getDomainColor,
  getDomainBadge,
} from '../hooks/useAthleteReadiness';
import { BODY_ZONE_LABELS } from '../../enums/bodyZoneLabels';
import { formatJump, jumpDisplayValue } from '../utils/jumpUnit';
import { classifyRI } from '@leopart/calc';

// ─── Complaints helpers ────────────────────────────────────────────────────────

const TYPE_LABELS: Record<string, string> = {
  pain: 'Dolore',
  stiffness: 'Rigidità',
  swelling: 'Gonfiore',
  weakness: 'Debolezza',
};

function getImpactClass(impact: string): string {
  if (impact === 'cannot_do_all') return styles.complaintTagImpactDanger;
  if (impact === 'will_limit') return styles.complaintTagImpactWarn;
  return styles.complaintTagImpact;
}

function getImpactLabel(impact: string): string {
  if (impact === 'cannot_do_all') return 'Non riesce';
  if (impact === 'will_limit') return 'Limitato';
  return 'OK';
}

// ─── UI Sub-components ────────────────────────────────────────────────────────

function ZoneBadge({ label, color }: { label: string; color: 'pos' | 'neg' | 'neutral' | '4' }) {
  const bgColors = { pos: '#eff9f4', neg: '#fbf2f1', neutral: '#fff7ee', '4': '#effafe' };
  const textColors = { pos: '#27ae60', neg: '#f57c73', neutral: '#ffa94d', '4': '#25a2da' };

  return (
    <div className={styles.zoneBadge} style={{ backgroundColor: bgColors[color] }}>
      <p className={styles.zoneBadgeText} style={{ color: textColors[color] }}>{label}</p>
    </div>
  );
}

function CustomChartTooltip({ active, payload, benchmark, testType }: any) {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0].payload;
  const value = data.rsi;
  if (!value) return null;

  const zone = getZone(value, benchmark.mean, benchmark.stdDev);

  return (
    <div className={styles.chartTooltip}>
      <p className={styles.chartTooltipDate}>{data.date}</p>
      <div className={styles.chartTooltipBadge}>
        <ZoneBadge color={getZoneBadgeColor(zone.label)} label={zone.label} />
      </div>
      <p className={styles.chartTooltipValue}>
        {testType === 'test-cmj' ? `CMJ : ${formatJump(value, data.cmjUnit)}` : `RSI : ${value.toFixed(2)}`}
      </p>
    </div>
  );
}

// Soglie RI canoniche da @leopart/calc (70/50/30, le stesse del backend) —
// qui vive solo la mappa status → colore/etichetta di questo chart.
const RI_STATUS_COLOR: Record<ReturnType<typeof classifyRI>, string> = {
  good: '#27ae60',
  normal: '#FFD15A',
  warning: '#FF9F43',
  critical: '#f57c73',
};
const RI_STATUS_LABEL: Record<ReturnType<typeof classifyRI>, string> = {
  good: 'Ottimo',
  normal: 'Buono',
  warning: 'Medio',
  critical: 'Sotto stress',
};

function RiCustomDot({ cx, cy, value }: any) {
  if (value == null || !cx || !cy) return null;
  const color = RI_STATUS_COLOR[classifyRI(value)];
  return <circle cx={cx} cy={cy} r={5} fill={color} stroke="#fff" strokeWidth={2} />;
}

function RiTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const { date, ri } = payload[0].payload;
  const color = RI_STATUS_COLOR[classifyRI(ri)];
  const label = RI_STATUS_LABEL[classifyRI(ri)];
  return (
    <div className={styles.chartTooltip}>
      <p className={styles.chartTooltipDate}>{date}</p>
      <p className={styles.chartTooltipValue} style={{ color }}>RI: {ri.toFixed(0)}</p>
      <p style={{ fontSize: 11, color, margin: 0, fontFamily: "'Satoshi', sans-serif" }}>{label}</p>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface AthleteReadinessViewProps {
  athleteId: string;
  selectedDate: string;
}

export default function AthleteReadinessView({ athleteId, selectedDate }: AthleteReadinessViewProps) {
  const {
    isPending,
    hooper,
    cmj,
    fcr,
    hrv,
    bodyMap,
    ri,
    benchmark,
    cmjHistoryCount,
    cmjDisplayValue,
    computedZone,
    benchmarkZone,
    testPreferences,
    chartData,
    chartDataWithLevels,
    yDomain,
    riChartData,
    sleepChartData,
    history,
    paginatedHistory,
    startIndex,
    currentPage,
    setCurrentPage,
    totalPages,
    historyLimit,
    setHistoryLimit,
  } = useAthleteReadiness({ athleteId, selectedDate });

  const complaints = bodyMap?.complaints ?? [];

  // ─── Loading state ────────────────────────────────────────────────────────

  if (isPending) {
    return (
      <div className={styles.root}>
        <div className={styles.headerRow}>
          <h2 className={styles.sectionTitle}>Readiness Monitoring</h2>
        </div>
        <div className={styles.twoColGrid}>
          <div className={styles.card}>
            <div className={styles.skeletonBlock} style={{ width: '40%', height: 14, marginBottom: 16 }} />
            <div className={styles.skeletonBlock} style={{ width: '30%', height: 48, margin: '0 auto 24px' }} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              {[1, 2, 3, 4].map(i => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <div className={styles.skeletonBlock} style={{ width: 32, height: 32, borderRadius: '50%' }} />
                  <div className={styles.skeletonBlock} style={{ width: '60%', height: 10 }} />
                  <div className={styles.skeletonBlock} style={{ width: '40%', height: 10 }} />
                </div>
              ))}
            </div>
          </div>
          <div className={styles.card}>
            <div className={styles.skeletonBlock} style={{ width: '60%', height: 14, marginBottom: 20 }} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              {[1, 2, 3, 4].map(i => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div className={styles.skeletonBlock} style={{ width: '70%', height: 10 }} />
                  <div className={styles.skeletonBlock} style={{ width: '50%', height: 20 }} />
                </div>
              ))}
            </div>
            <div className={styles.skeletonBlock} style={{ width: '100%', height: 8, marginTop: 20, borderRadius: 4 }} />
          </div>
        </div>
        <div className={styles.dataGrid}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} className={styles.cardSm}>
              <div className={styles.skeletonBlock} style={{ width: '50%', height: 12, marginBottom: 16 }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className={styles.skeletonBlock} style={{ width: '100%', height: 10 }} />
                <div className={styles.skeletonBlock} style={{ width: '80%', height: 10 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className={styles.root}>
      {/* Header */}
      {/* <div className={styles.headerRow}>
        <h2 className={styles.sectionTitle}>Readiness Monitoring</h2>
      </div> */}

      {/* Two-column layout: RI Score and Benchmark side by side */}
      <div className={styles.twoColGrid}>
        {/* RI Score Overview */}
        <div className={styles.card}>
          <div className={styles.riCardInner}>
            <p className={styles.riLabel}>Readiness Index</p>
            <p className={styles.riScore} style={{ color: getDomainColor(ri.value) }}>
              {ri.value}
            </p>

            <div className={styles.domainsGrid}>
              {/* Mental Domain — driven by hooper interpolation */}
              <div className={styles.domainCell}>
                <div className={styles.domainIconRow}>
                  <Brain className={styles.domainIcon} />
                </div>
                <p className={styles.domainLabel}>Mentale</p>
                <p className={styles.domainBadge} style={{ color: getDomainColor(hooper?.interpolation) }}>
                  {getDomainBadge(hooper?.interpolation)}
                </p>
                {hooper && (
                  <div className={styles.tooltip}>
                    <div className={styles.tooltipInner}>
                      <p className={styles.tooltipTitle}>Mentale</p>
                      <p className={styles.tooltipDesc}>
                        Stress: {hooper.stress ?? '-'} / Umore: n.d.
                      </p>
                      <div className={styles.tooltipArrow} />
                    </div>
                  </div>
                )}
              </div>

              {/* Muscular Domain — driven by CMJ interpolation */}
              <div className={styles.domainCell}>
                <div className={styles.domainIconRow}>
                  <Dumbbell className={styles.domainIcon} />
                </div>
                <p className={styles.domainLabel}>Muscolare</p>
                <p className={styles.domainBadge} style={{ color: getDomainColor(cmj?.interpolation) }}>
                  {getDomainBadge(cmj?.interpolation)}
                </p>
                {cmj && (
                  <div className={styles.tooltip}>
                    <div className={styles.tooltipInner}>
                      <p className={styles.tooltipTitle}>Muscolare</p>
                      <p className={styles.tooltipDesc}>
                        CMJ: {formatJump(cmj.rawValue, cmj.cmjUnit)} / DOMS: {hooper?.doms ?? '-'}
                      </p>
                      <div className={styles.tooltipArrow} />
                    </div>
                  </div>
                )}
              </div>

              {/* Cardio Domain — driven by FCR interpolation */}
              <div className={styles.domainCell}>
                <div className={styles.domainIconRow}>
                  <Heart className={styles.domainIcon} />
                </div>
                <p className={styles.domainLabel}>Cardio</p>
                <p className={styles.domainBadge} style={{ color: getDomainColor(fcr?.interpolation) }}>
                  {getDomainBadge(fcr?.interpolation)}
                </p>
                {(fcr || hrv) && (
                  <div className={styles.tooltip}>
                    <div className={styles.tooltipInner}>
                      <p className={styles.tooltipTitle}>Cardiovascolare</p>
                      <p className={styles.tooltipDesc}>
                        HRrest: {fcr?.rawValue ?? '-'} / HRV: {hrv?.rawValue?.toFixed(0) ?? '-'}
                      </p>
                      <div className={styles.tooltipArrow} />
                    </div>
                  </div>
                )}
              </div>

              {/* Strain Domain — no dedicated v2 factor, show HRV interpolation as proxy */}
              <div className={styles.domainCell}>
                <div className={styles.domainIconRow}>
                  <Zap className={styles.domainIcon} />
                </div>
                <p className={styles.domainLabel}>Strain</p>
                <p className={styles.domainBadge} style={{ color: getDomainColor(hrv?.interpolation) }}>
                  {getDomainBadge(hrv?.interpolation)}
                </p>
                {hrv?.rawValue != null && (
                  <p style={{ fontSize: 11, color: 'var(--c-text-1)', marginTop: 2, textAlign: 'center' }}>
                    {hrv.rawValue.toFixed(0)} ms
                    {hrv.weeklyCV != null && (
                      <span style={{
                        marginLeft: 4,
                        color: hrv.weeklyCV < 5 ? '#22c55e' : hrv.weeklyCV < 10 ? '#4d4d4d' : hrv.weeklyCV < 15 ? '#f59e0b' : '#ef4444',
                      }}>
                        CV {hrv.weeklyCV.toFixed(1)}%
                      </span>
                    )}
                  </p>
                )}
                {hrv && (
                  <div className={styles.tooltip}>
                    <div className={styles.tooltipInner}>
                      <p className={styles.tooltipTitle}>Strain (HRV proxy)</p>
                      <p className={styles.tooltipDesc}>
                        HRV oggi: {hrv.rawValue?.toFixed(0) ?? '-'} ms
                        {hrv.baselineMean != null ? ` (media 31gg: ${hrv.baselineMean.toFixed(0)} ms)` : ''}
                      </p>
                      <p className={styles.tooltipDesc}>
                        CV 31gg: {hrv.weeklyCV?.toFixed(1) ?? '-'}% —{' '}
                        {hrv.cvStatus === 'stable' ? 'stabile' : hrv.cvStatus === 'moderate' ? 'variabilità moderata' : hrv.cvStatus === 'high' ? 'variabilità alta' : '-'}
                      </p>
                      <div className={styles.tooltipArrow} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Benchmark Card */}
        <div className={styles.card}>
          <p className={styles.benchmarkTitle}>
            Benchmark personale - {testPreferences.rsiTest === 'test-cmj' ? 'CMJ RSI' : 'Drop Jump RSI'}
          </p>

          {(benchmark.mean > 0 || benchmark.lastValue > 0) ? (
            <>
              <div className={styles.benchmarkGrid4}>
                {cmjHistoryCount >= 2 && (
                  <div className={styles.benchmarkItem}>
                    <p className={styles.benchmarkLabel}>Media (μ)</p>
                    <p className={styles.benchmarkValueLg}>{benchmark.mean.toFixed(2)}</p>
                  </div>
                )}
                {cmjHistoryCount >= 2 && (
                  <div className={styles.benchmarkItem}>
                    <p className={styles.benchmarkLabel}>Dev. std (σ)</p>
                    <p className={styles.benchmarkValueLg}>{benchmark.stdDev.toFixed(2)}</p>
                  </div>
                )}
                {cmjHistoryCount >= 2 && (
                  <div className={styles.benchmarkItem}>
                    <p className={styles.benchmarkLabel}>Personal Best</p>
                    <p className={styles.benchmarkValueGreen}>{benchmark.personalBest.toFixed(2)}</p>
                  </div>
                )}
                {/* Ultimo test: ultimo valore storico CMJ con data dal backend */}
                <div className={styles.benchmarkItem}>
                  <div className={styles.prevRow}>
                    <p className={styles.prevLabel}>Ultimo test</p>
                    {benchmark.lastValueDate && (
                      <div className={styles.infoWrapper}>
                        <svg className={styles.infoIcon} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div className={styles.infoTooltip}>
                          {new Date(benchmark.lastValueDate).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}
                          <div className={styles.infoTooltipArrow} />
                        </div>
                      </div>
                    )}
                  </div>
                  <p className={styles.benchmarkValueLg} style={{ color: benchmarkZone.color }}>
                    {benchmark.lastValue > 0 ? benchmark.lastValue.toFixed(2) : '-'}
                  </p>
                </div>
                {/* Valore attuale: valore CMJ del giorno selezionato (solo se presente) */}
                {cmj?.rawValue != null && (
                  <div className={styles.benchmarkItem}>
                    <p className={styles.benchmarkLabel}>Valore attuale</p>
                    <p className={styles.benchmarkValueGreen}>{cmj.rawValue.toFixed(2)}</p>
                  </div>
                )}
              </div>

              {/* Zone Bar */}
              <div className={styles.zoneBar}>
                <div className={styles.zoneBarStripes}>
                  <div className={styles.zoneSegment} style={{ background: '#f57c73', opacity: 0.3 }} />
                  <div className={styles.zoneSegment} style={{ background: '#FFD15A', opacity: 0.3 }} />
                  <div className={styles.zoneSegment} style={{ background: '#5BC9E0', opacity: 0.3 }} />
                  <div className={styles.zoneSegment} style={{ background: '#27ae60', opacity: 0.3 }} />
                </div>
                {cmjDisplayValue > 0 && (
                  <div
                    className={styles.zoneIndicator}
                    style={{
                      backgroundColor: benchmarkZone.color,
                      left: `${Math.min(95, Math.max(5,
                        benchmark.stdDev > 0
                          ? ((cmjDisplayValue - (benchmark.mean - benchmark.stdDev * 2)) / (benchmark.stdDev * 4)) * 100
                          : cmjDisplayValue >= benchmark.mean ? 87 : 50
                      ))}%`
                    }}
                  />
                )}
              </div>

              <div className={styles.zoneLabels}>
                <span className={styles.zoneLabelRed}>Bassa</span>
                <span className={styles.zoneLabelYellow}>Media</span>
                <span className={styles.zoneLabelBlue}>Buona</span>
                <span className={styles.zoneLabelGreen}>Ottima</span>
              </div>

              <div className={styles.zoneBadgeRow}>
                <ZoneBadge
                  color={getZoneBadgeColor(computedZone)}
                  label={`Zona: ${computedZone}`}
                />
              </div>
            </>
          ) : (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateInner}>
                <p className={styles.emptyTitle}>Nessun dato disponibile</p>
                <p className={styles.emptySubtitle}>Non ci sono test fino a questa data</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Complaints card — shown only when the athlete has reported ailments */}
      {complaints.length > 0 && (
        <div className={styles.complaintsCard}>
          <div className={styles.complaintsCardHeader}>
            <TriangleAlert className={styles.complaintsCardIcon} />
            <p className={styles.complaintsCardTitle}>
              Fastidi segnalati oggi ({complaints.length})
            </p>
          </div>
          <div className={styles.complaintsGrid}>
            {complaints.map((c, i) => (
              <div key={i} className={styles.complaintItem}>
                <p className={styles.complaintZone}>
                  {BODY_ZONE_LABELS[c.zone] ?? c.zone}
                </p>
                <p className={styles.complaintIntensity}>
                  Intensità: {c.intensity}/5
                </p>
                <div className={styles.complaintMeta}>
                  <span className={[styles.complaintTag, styles.complaintTagType].join(' ')}>
                    {TYPE_LABELS[c.type] ?? c.type}
                  </span>
                  <span className={[styles.complaintTag, getImpactClass(c.impact)].join(' ')}>
                    {getImpactLabel(c.impact)}
                  </span>
                </div>
              </div>
            ))}
          </div>
          {bodyMap?.incoherenceWarning && (
            <p className={styles.complaintIncoherence}>
              ⚠ {bodyMap.incoherenceWarning}
            </p>
          )}
        </div>
      )}

      {/* Data Entry - Read-only Fields */}
      <div className={styles.dataGrid}>
        {/* Mental Domain */}
        <div className={styles.cardSm}>
          <p className={styles.cardTitle}>Mentale (PRE)</p>
          <div className={styles.fieldList}>
            <div className={styles.fieldRow}>
              <p className={styles.fieldLabel}>Stress (1-5)</p>
              <p className={styles.fieldValue}>{hooper?.stress ?? '-'}</p>
            </div>
            <div className={styles.fieldRow}>
              <p className={styles.fieldLabel}>Sonno (1-5)</p>
              <p className={styles.fieldValue}>{hooper?.sleepQuality ?? '-'}</p>
            </div>
            <div className={styles.fieldRow}>
              <p className={styles.fieldLabel}>Ore sonno</p>
              <p className={styles.fieldValue}>
                {hooper?.sleepHours != null ? `${hooper.sleepHours}h` : '-'}
              </p>
            </div>
          </div>
        </div>

        {/* Muscular Domain */}
        <div className={styles.cardSm}>
          <p className={styles.cardTitle}>Muscolare (PRE)</p>
          <div className={styles.fieldList}>
            <div className={styles.fieldRow}>
              <p className={styles.fieldLabel}>CMJ ({cmj?.cmjUnit === 'in' ? 'in' : 'cm'})</p>
              <p className={styles.fieldValue}>
                {cmj?.rawValue != null ? jumpDisplayValue(cmj.rawValue, cmj.cmjUnit).toFixed(1) : '-'}
              </p>
            </div>
            <div className={styles.fieldRow}>
              <p className={styles.fieldLabel}>DOMS (1-5)</p>
              <p className={styles.fieldValue}>{hooper?.doms ?? '-'}</p>
            </div>
          </div>
        </div>

        {/* Cardio Domain */}
        <div className={styles.cardSm}>
          <p className={styles.cardTitle}>Cardiovascolare (PRE)</p>
          <div className={styles.fieldList}>
            <div className={styles.fieldRow}>
              <p className={styles.fieldLabel}>HRrest</p>
              <p className={styles.fieldValue}>{fcr?.rawValue ?? '-'} {fcr?.rawValue ? 'bpm' : ''}</p>
            </div>
            <div className={styles.fieldRow}>
              <p className={styles.fieldLabel}>HRV</p>
              <p className={styles.fieldValue}>{hrv?.rawValue ?? '-'} {hrv?.rawValue ? 'ms' : ''}</p>
            </div>
          </div>
        </div>

        {/* Strain (POST) — sRPE / ACWR not available in v2; show HRV CV as proxy */}
        <div className={styles.cardSm}>
          <p className={styles.cardTitle}>Strain (POST)</p>
          <div className={styles.fieldList}>
            <div className={styles.fieldRow}>
              <p className={styles.fieldLabel}>Fatica (1-5)</p>
              <p className={styles.fieldValue}>{hooper?.fatigue ?? '-'}</p>
            </div>
            <div className={styles.fieldRow}>
              <div className={styles.prevRow}>
                <p className={styles.fieldLabel}>HRV CV%</p>
                <div className={styles.infoWrapper}>
                  <svg className={styles.infoIcon} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className={styles.infoTooltip}>
                    CV = (σ/μ)×100 degli ultimi 7gg di HRV<br />
                    Baseline: media 31gg da delay metrics<br />
                    &lt; 5%: molto stabile · 5-10%: normale<br />
                    10-15%: moderata · &gt; 15%: alta
                    <div className={styles.infoTooltipArrow} />
                  </div>
                </div>
              </div>
              <p
                className={styles.fieldValue}
                style={{
                  color: hrv?.cvStatus === 'stable' ? '#27ae60' : hrv?.cvStatus === 'moderate' ? '#FFD15A' : hrv?.cvStatus === 'high' ? '#f57c73' : undefined,
                  fontWeight: hrv?.cvStatus ? 600 : undefined,
                }}
              >
                {hrv?.weeklyCV?.toFixed(1) ?? '-'}{hrv?.weeklyCV != null ? '%' : ''}
              </p>
            </div>
            {hrv?.baselineMean != null && (
              <div className={styles.fieldRow}>
                <p className={styles.fieldLabel}>HRV baseline</p>
                <p className={styles.fieldValue} style={{ fontSize: 11, color: '#888' }}>
                  {hrv.baselineMean.toFixed(0)} ± {hrv.baselineStdDev?.toFixed(0) ?? '?'} ms
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Storico Sessioni */}
      {history.length > 0 && (
        <>
          <h2 className={styles.sectionTitle}>Storico sessioni</h2>

          {/* Grafico Trend RSI */}
          {/* Selettore periodo storico */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 12, color: 'var(--c-text-1)', fontWeight: 600 }}>Periodo:</span>
            {[
              { label: '7gg', value: 7 },
              { label: '28gg', value: 28 },
              { label: '90gg', value: 90 },
              { label: '1 anno', value: 365 },
            ].map(({ label, value }) => (
              <button
                key={value}
                type="button"
                onClick={() => { setHistoryLimit(value); setCurrentPage(1); }}
                style={{
                  padding: '3px 10px',
                  fontSize: 11,
                  fontWeight: 600,
                  borderRadius: 20,
                  border: historyLimit === value ? 'none' : '1px solid #e7e7e7',
                  background: historyLimit === value ? '#063444' : 'transparent',
                  color: historyLimit === value ? '#fff' : '#4d4d4d',
                  cursor: 'pointer',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {riChartData.length > 0 && (
            <div className={styles.historyChartCard}>
              <p className={styles.historyChartTitle}>
                Andamento Recovery Index — Ultimi {historyLimit} giorni
              </p>
              <div className={styles.historyChartContainer}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={riChartData} margin={{ top: 10, right: 30, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e7e7" vertical={false} />
                    <XAxis dataKey="date" tick={{ fill: '#4d4d4d', fontSize: 10, fontFamily: "'Satoshi:Medium',sans-serif" }} tickLine={false} angle={-45} textAnchor="end" height={60} />
                    <YAxis domain={[0, 100]} tick={{ fill: '#4d4d4d', fontSize: 10, fontFamily: "'Satoshi:Medium',sans-serif" }} tickLine={false} axisLine={false} />
                    <Tooltip content={<RiTooltip />} />
                    <ReferenceLine y={90} stroke="#27ae60" strokeDasharray="4 4" strokeWidth={1.5} label={{ value: 'Ottimo', position: 'insideTopRight', fill: '#27ae60', fontSize: 10, fontFamily: "'Satoshi', sans-serif" }} />
                    <ReferenceLine y={75} stroke="#FFD15A" strokeDasharray="4 4" strokeWidth={1.5} label={{ value: 'Buono', position: 'insideTopRight', fill: '#FFD15A', fontSize: 10, fontFamily: "'Satoshi', sans-serif" }} />
                    <ReferenceLine y={60} stroke="#FF9F43" strokeDasharray="4 4" strokeWidth={1.5} label={{ value: 'Medio', position: 'insideTopRight', fill: '#FF9F43', fontSize: 10, fontFamily: "'Satoshi', sans-serif" }} />
                    <Line
                      type="monotone"
                      dataKey="ri"
                      stroke="#063444"
                      strokeWidth={3}
                      dot={<RiCustomDot />}
                      activeDot={{ r: 7 }}
                      name="Recovery Index"
                      connectNulls={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {chartData.length > 0 && (
            <div className={styles.historyChartCard}>
              <p className={styles.historyChartTitle}>
                Storico {testPreferences.rsiTest === 'test-cmj' ? 'CMJ' : 'RSI'} - Ultimi {historyLimit} giorni
              </p>
              <div className={styles.historyChartContainer}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartDataWithLevels} margin={{ top: 10, right: 30, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e7e7" vertical={false} />
                    <XAxis dataKey="date" tick={{ fill: '#4d4d4d', fontSize: 10, fontFamily: "'Satoshi:Medium',sans-serif" }} tickLine={false} angle={-45} textAnchor="end" height={60} />
                    <YAxis domain={yDomain} tick={{ fill: '#4d4d4d', fontSize: 10, fontFamily: "'Satoshi:Medium',sans-serif" }} tickLine={false} axisLine={false} />
                    <Tooltip content={<CustomChartTooltip benchmark={benchmark} testType={testPreferences.rsiTest} />} />
                    <Line type="monotone" dataKey="excellent" stroke="#27ae60" strokeDasharray="5 5" strokeWidth={2} dot={false} name="Eccellente" legendType="none" />
                    <Line type="monotone" dataKey="good" stroke="#5BC9E0" strokeDasharray="5 5" strokeWidth={2} dot={false} name="Buono" legendType="none" />
                    <Line type="monotone" dataKey="medium" stroke="#FFD15A" strokeDasharray="5 5" strokeWidth={2} dot={false} name="Medio" legendType="none" />
                    <Line type="monotone" dataKey="low" stroke="#f57c73" strokeDasharray="5 5" strokeWidth={2} dot={false} name="Basso" legendType="none" />
                    <Line type="monotone" dataKey="rsi" stroke="#013344" strokeWidth={3} dot={{ fill: '#013344', r: 5, strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 7 }} name={testPreferences.rsiTest === 'test-cmj' ? 'CMJ' : 'RSI'} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {sleepChartData.length > 0 && (
            <div className={styles.historyChartCard}>
              <p className={styles.historyChartTitle}>
                Storico ore di sonno - Ultimi {historyLimit} giorni
              </p>
              <div className={styles.historyChartContainer}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={sleepChartData} margin={{ top: 10, right: 30, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e7e7" vertical={false} />
                    <XAxis dataKey="date" tick={{ fill: '#4d4d4d', fontSize: 10, fontFamily: "'Satoshi:Medium',sans-serif" }} tickLine={false} angle={-45} textAnchor="end" height={60} />
                    <YAxis domain={[0, 12]} tick={{ fill: '#4d4d4d', fontSize: 10, fontFamily: "'Satoshi:Medium',sans-serif" }} tickLine={false} axisLine={false} unit="h" />
                    <Tooltip formatter={(value) => [`${value}h`, 'Ore sonno']} labelStyle={{ fontFamily: "'Satoshi', sans-serif", fontSize: 12 }} />
                    <Line type="monotone" dataKey="sleepHours" stroke="#5BC9E0" strokeWidth={3} dot={{ fill: '#5BC9E0', r: 4, strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 6 }} name="Ore sonno" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className={styles.historyTableCard}>
            <div className={styles.tableScrollWrapper}>
              <table className={styles.table}>
                <thead className={styles.thead}>
                  <tr>
                    <th className={styles.thLeft}><p className={styles.thLabel}>DATA</p></th>
                    <th className={styles.thCenter}><p className={styles.thLabel}>TEST</p></th>
                    <th className={styles.thCenter}><p className={styles.thLabel}>VALORE</p></th>
                    <th className={styles.thCenter}><p className={styles.thLabel}>Δ PREC.</p></th>
                    <th className={styles.thCenter}><p className={styles.thLabel}>ZONA</p></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedHistory.map((session, idx) => {
                    const actualIndex = startIndex + idx;
                    const previousSession = history[actualIndex + 1];

                    const value = session.cmj ?? null;
                    const previousValue = previousSession?.cmj ?? null;

                    const delta = value != null && previousValue != null ? value - previousValue : null;
                    const zone = value != null ? getZone(value, benchmark.mean, benchmark.stdDev) : null;

                    return (
                      <tr key={session.date} className={styles.tableRow}>
                        <td className={styles.tdLeft}>
                          <p className={styles.cellDate}>
                            {new Date(session.date).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                        </td>
                        <td className={styles.tdCenter}>
                          <div className={styles.typeBadge}>
                            <p className={styles.typeBadgeText}>{session.cmj != null ? 'CMJ' : 'Hooper'}</p>
                          </div>
                        </td>
                        <td className={styles.tdCenter}>
                          <p className={styles.cellValue}>
                            {value != null ? formatJump(value, session.cmjUnit) : (session.hooper != null ? session.hooper.toFixed(1) : '-')}
                          </p>
                        </td>
                        <td className={styles.tdCenter}>
                          {delta !== null ? (
                            <p className={delta > 0 ? styles.deltaPos : delta < 0 ? styles.deltaNeg : styles.deltaZero}>
                              {delta > 0 ? '+' : ''}{delta.toFixed(2)}
                            </p>
                          ) : (
                            <p className={styles.emptyCell}>-</p>
                          )}
                        </td>
                        <td className={styles.tdCenter}>
                          {zone && (
                            <div className={styles.zoneBadgeInline}>
                              <ZoneBadge color={getZoneBadgeColor(zone.label)} label={zone.label} />
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className={styles.pageNavBtn}>
                  <svg className={styles.pageNavIcon} fill="none" viewBox="0 0 32 32">
                    <path d="M23 16H9M9 16L15 10M9 16L15 22" stroke="#4d4d4d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                  <button key={pageNum} onClick={() => setCurrentPage(pageNum)} className={cx(styles.pageBtn, currentPage === pageNum && styles.pageBtnActive)}>
                    <p className={styles.pageBtnText}>{pageNum}</p>
                  </button>
                ))}
                <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} className={styles.pageNavBtn}>
                  <svg className={styles.pageNavIcon} fill="none" viewBox="0 0 32 32">
                    <path d="M9 16H23M23 16L17 10M23 16L17 22" stroke="#4d4d4d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
